/**
 * Rasterizing the map's DOM instead taints the canvas on WebKit — mapbox's control backgrounds are
 * escaped `data:image/svg+xml` URIs — and `toBlob` then throws SecurityError (STX1-1162). A WebGL
 * canvas cannot be tainted. The copy is not optional either: `toBlob` is async, and the live canvas
 * repaints under it.
 */

const FRAME_TIMEOUT_MS = 100

interface IFrameCopyOptions {
  /** Centre-crop to exactly this size, in CSS pixels. */
  cropSize?: { width: number; height: number }
  /** Output density; defaults to the map's own. */
  pixelRatio?: number
  /** Shift the crop up by this many CSS px, to frame something anchored below its own mass. */
  liftPx?: number
}

/** Read off the canvas alone: an ancestor CSS transform would desync CSS px from the backing store. */
export function frameDensity(map: mapboxgl.Map): number {
  const gl = map.getCanvas()
  return gl.clientWidth > 0 ? gl.width / gl.clientWidth : 1
}

// Uniform, never per axis: the mini-map is fluid-width but a fixed 120 tall, so clamping width
// alone drew a squat rect into a wide box. Costs resolution instead of proportion.
const frameFit = (
  gl: HTMLCanvasElement,
  density: number,
  cropSize?: { width: number; height: number },
): number =>
  cropSize
    ? Math.min(
        1,
        gl.width / (cropSize.width * density),
        gl.height / (cropSize.height * density),
      )
    : 1

/** The CSS width the crop really shows — a canvas narrower than cropSize shrinks the window. */
export function capturedCssWidth(
  map: mapboxgl.Map,
  cropSize?: { width: number; height: number },
): number {
  const gl = map.getCanvas()
  return cropSize
    ? cropSize.width * frameFit(gl, frameDensity(map), cropSize)
    : gl.clientWidth
}

export function copyMapFrame(
  map: mapboxgl.Map,
  { cropSize, pixelRatio, liftPx = 0 }: IFrameCopyOptions = {},
): HTMLCanvasElement {
  const gl = map.getCanvas()
  // Client dimensions size the output, and a hidden subtree reports 0 while the buffer holds
  // the last frame — that emitted a 1x1 image.
  if (!gl?.width || !gl.height || !gl.clientWidth || !gl.clientHeight) {
    return null
  }
  const density = pixelRatio ?? frameDensity(map)

  // 1:1, so a zoom number describes one amount of ground on any display. Squeezing the whole
  // frame into cropSize made the printed scale follow the window instead — ~2 levels wider on a
  // 1600px map. STX1-959 sized this to mirror the viewport, which was right while the camera was
  // wherever the user was looking; it is chosen deliberately now, so the framing follows the zoom.
  const fit = frameFit(gl, density, cropSize)
  const sourceWidth = cropSize ? cropSize.width * density * fit : gl.width
  const sourceHeight = cropSize ? cropSize.height * density * fit : gl.height

  // CSS px times density, never the backing store: a 1x display would otherwise halve the output.
  const size = cropSize ?? { width: gl.clientWidth, height: gl.clientHeight }
  const out = document.createElement('canvas')
  out.width = Math.max(1, Math.round(size.width * density))
  out.height = Math.max(1, Math.round(size.height * density))

  const ctx = out.getContext('2d')
  if (!ctx) {
    return null
  }
  // Clamped, so the lift can never walk the crop off the top of the frame.
  const top = Math.max(0, (gl.height - sourceHeight) / 2 - liftPx * density)
  ctx.drawImage(
    gl,
    (gl.width - sourceWidth) / 2,
    top,
    sourceWidth,
    sourceHeight,
    0,
    0,
    out.width,
    out.height,
  )
  return out
}

/** WebKit frees a canvas's pixels only on GC or once it is zeroed, against one budget for all. */
export function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0
  canvas.height = 0
}

/**
 * Frees a live map's drawing buffers while something else stands in for it, keeping the map, its
 * tiles and its camera. Returns the undo, which sizes the canvas back and repaints, unless the map
 * resized it meanwhile. map.resize() would not: it skips a container whose size has not changed.
 */
export function releaseMapCanvas(map: mapboxgl.Map): () => void {
  const canvas = map.getCanvas()
  const { width, height } = canvas
  releaseCanvas(canvas)
  return () => {
    if (canvas.width === 0 && canvas.height === 0) {
      canvas.width = width
      canvas.height = height
      map.triggerRepaint()
    }
  }
}

// rAF never fires while the tab is hidden, and these waits gate the capture and remove the still.
export function nextFrame(): Promise<void> {
  return new Promise<void>(resolve => {
    let timer: ReturnType<typeof setTimeout>
    const done = (): void => {
      clearTimeout(timer)
      resolve()
    }
    timer = setTimeout(done, FRAME_TIMEOUT_MS)
    requestAnimationFrame(done)
  })
}

/**
 * Returns the undo. Swallows gestures too: a pan the user cannot see is only yanked back.
 * `maxDensity` caps the still's resolution where its memory matters more than its sharpness.
 */
export function freezeMapFrame(
  map: mapboxgl.Map,
  maxDensity = Infinity,
): () => void {
  const gl = map.getCanvas()
  const still = copyMapFrame(map, {
    pixelRatio: Math.min(frameDensity(map), maxDensity),
  })
  if (!still) {
    return () => undefined
  }
  // Removal waits a frame: jumpTo only schedules a render, so the buffer still holds the
  // overview pixels when the restore returns.
  still.style.cssText = `position:absolute;top:0;left:0;width:${gl.clientWidth}px;height:${gl.clientHeight}px;z-index:5`
  map.getContainer().appendChild(still)
  // Zeroed as well as removed: WebKit holds a canvas's pixels until GC otherwise.
  return () =>
    nextFrame().then(() => {
      still.remove()
      releaseCanvas(still)
    })
}
